/**
 * Wake-on-screen configuration for the office app (see packages/offline-sdk/src/wake/wakeCallables.ts
 * for what a knock is: an OPTIONS request that onCall's CORS layer answers before the handler, so it
 * wakes the function and logs no ERROR).
 *
 * Every list below is the set of callables a user can reach from that screen whose pre-flight was
 * measured slow (>= 8 s) at least once in the 7-day baseline, ranked by how often.
 * The rollout is wave by wave; each wave adds screens here and is measured against the control.
 */
// Import text MUST contain 'config/' (not './firebase'): client-tech's Vite stub plugin redirects by
// import text, and a './firebase' import pulls the SHARED firebase.ts into the Technician bundle,
// which runs a second initializeApp (app/duplicate-app).
import { functions } from '../config/firebase';
import { wakeKnocksOff, wakePilotControlPercentRaw, wakeUsingEmulators } from '../config/wakeFlags';

/** Global kill switch: build with REACT_APP_WAKE_KNOCKS=off (office app) or VITE_WAKE_KNOCKS=off (Technician app) and no screen knocks anywhere. */
export const WAKE_KNOCKS_ENABLED = !wakeKnocksOff();

/** Per-screen switches, so one screen can be A/B'd or turned off without touching the others. */
export const WAKE_SCREENS = {
  ticketDetail: {
    enabled: true,
    callables: [
      'fieldService_ticketNextStep',
      'updateServiceLog',
      'createServicePart',
      'fieldService_createServiceTicket',
      'fieldService_deleteTicketPart',
      'fieldService_advanceTicket',
      'updateServicePart',
      'fieldService_createQuoteSheet',
      'fieldService_galleryPromote',
      'fieldService_attachGeneratorContact',
      // WAKE-2a: read on the dashboard a user goes BACK to from a ticket (the knock here comes before that return).
      'fieldService_needsAttention',
    ],
  },
  generatorDetail: {
    enabled: true,
    callables: [
      'fieldService_updateUnitRecord',
      'fieldService_createServiceTicket',
      'fieldService_attachGeneratorContact',
      'fieldService_galleryPromote',
      'fieldService_galleryHide',
      'fieldService_gallerySetPrimary',
      'fieldService_createRecurringSchedules',
      'fieldService_addBomEntry',
      'fieldService_setRecurringScheduleArchived',
      'fieldService_deleteGeneratorPricingEntry',
      'updateSharedContact',
      // fieldService_setRecurringSchedulePaused (AS-GAPS PR1) is deliberately NOT here: this list is AT the 12-name cap and a pause is a rare,
      // deliberate click like its sibling archive. Add it only by a PR that also drops a name and says why.
      // WAKE-2a: the generator edit save. This list is now AT the 12-name cap.
      'updateGenerator',
    ],
  },
} as const;

/** One screen's knock list. Modules declare their own as plain constants (config/wake/<module>.ts). */
export interface WakeScreenDef {
  readonly enabled: boolean;
  readonly callables: readonly string[];
}

export type WakeScreen = keyof typeof WAKE_SCREENS;

const usingEmulators = wakeUsingEmulators();

/*
 * EXPECTED CONSOLE ERROR (ISS-595). Every knock shows up in DevTools as
 *   "Access to fetch at '.../<callable>?wake=1' ... has been blocked by CORS policy: Method OPTIONS is not allowed by
 *    Access-Control-Allow-Methods in preflight response."
 * It is not a failure and not an outage. The knock is a fetch whose METHOD is OPTIONS, so Chrome preflights it and asks
 * whether OPTIONS is allowed; firebase-functions answers every callable's preflight with `methods: "POST"` only
 * (firebase-functions 7.2.5, lib/v2/providers/https.js; no option to change it), so Chrome rejects the knock AFTER
 * the server has answered 204, which is the part that wakes the function. Checked in a real browser 2026-10-15/16: no
 * OPTIONS variant avoids the message (mode 'no-cors' throws before any request is sent), and page JavaScript cannot
 * suppress it. A real POST knock was rejected earlier (an ERROR log per knock). Triage ignores a message that is
 * exactly this line for a `wake=1` URL: functions/src/issueTriage/knownConsoleNoise.ts. The client's network error
 * capture also skips the knock's status-0 failure so it is never counted as a transport failure:
 * isExpectedWakeKnockFailure in client/src/lib/datahub/errorLogging/consoleAndNetworkCapture.ts (OFFICEHUB-CLIENT-75).
 * If you change the knock method or WAKE_URL_MARKER, change both rules with it.
 */

/**
 * Marks a request as a knock in Cloud Run's request log (`httpRequest.requestUrl` keeps the query
 * string; measured 2026-10-09). Without it a knock's own pre-flight, which absorbs the cold wait, is
 * indistinguishable from a real call's pre-flight and would inflate the "slow pre-flight" measure the
 * wake-up is judged by. The callable ignores the query, and the real call never carries it.
 */
export const WAKE_URL_MARKER = 'wake=1';

/**
 * The URL the Firebase SDK itself calls for this name (so the knock warms the same service the
 * real call uses), plus the knock marker. Stable reference: the hook re-subscribes when it changes.
 */
export function wakeUrlFor(name: string): string {
  const custom = functions.customDomain;
  const base = custom
    ? `${custom}/${name}`
    : `https://${functions.region}-${functions.app.options.projectId}.cloudfunctions.net/${name}`;
  return `${base}?${WAKE_URL_MARKER}`;
}

/**
 * PILOT SPLIT (machinery kept, default OFF). ORCH-12 ruled (2026-10-09) that a 50/50 split is not
 * worth it while server logs cannot tell the two arms apart (no uid in Cloud Run logs): the pilot
 * wakes EVERYONE and is read before/after against un-woken callables. The split stays so it can be
 * switched on once a client timing beacon exists (wave 1): the arm is a pure function of the uid
 * (same user, same arm, every device), the control share is the build flag
 * REACT_APP_WAKE_PILOT_CONTROL_PERCENT (0 = everyone woken, the default), and after `endsAt`
 * everyone is woken without another deploy.
 */
/** Share of users left UNwoken when the flag is not set: none (everyone is woken). */
export const DEFAULT_CONTROL_PERCENT = 0;

/**
 * The control share from the build flag. Blank or non-numeric means "not set" (the default), so
 * an env template that declares the variable empty cannot change who is woken.
 */
export function parseControlPercent(raw: string | undefined): number {
  if (raw === undefined || raw.trim() === '') return DEFAULT_CONTROL_PERCENT;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : DEFAULT_CONTROL_PERCENT;
}

export const WAKE_PILOT = {
  controlPercent: parseControlPercent(wakePilotControlPercentRaw()),
  endsAt: '2026-10-13T00:00:00Z',
} as const;

/** FNV-1a 32-bit: tiny, stable, no dependency; only the spread matters here, not secrecy. */
export function wakeBucket(uid: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < uid.length; i += 1) {
    h ^= uid.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h % 100;
}

export type WakeArm = 'wake' | 'control';

/** No uid (not signed in yet) is never woken: there is no one to assign an arm to. */
export function wakeArm(uid: string | null, nowMs: number = Date.now(), pilot: { controlPercent: number; endsAt: string } = WAKE_PILOT): WakeArm {
  if (uid === null) return 'control';
  if (nowMs >= Date.parse(pilot.endsAt)) return 'wake';
  return wakeBucket(uid) < pilot.controlPercent ? 'control' : 'wake';
}

/** Stable per-screen predicate (kill switch AND screen flag AND not on the emulator). */
export function wakeEnabledForDef(def: WakeScreenDef): () => boolean {
  return () => WAKE_KNOCKS_ENABLED && def.enabled && !usingEmulators;
}

/** Global gates only (build kill switch AND not on the emulator), for a caller whose lists carry their own flags. */
export function wakeKnocksAllowed(): boolean {
  return WAKE_KNOCKS_ENABLED && !usingEmulators;
}

/** The pilot screens by key. Kept for the existing callers and tests. */
export function wakeEnabledFor(screen: WakeScreen): () => boolean {
  return wakeEnabledForDef(WAKE_SCREENS[screen]);
}
